import { Presence } from '@radix-ui/react-presence';
import { memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

import { PrivateFiltersBar } from '~/components/Filters/v2/components/PrivateFiltersBar';
import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { PrivatePageSearch } from '~/components/Search/PrivatePageSearch';
import { useHeaderResize } from '~/components/v2/components/shared/table/useHeaderResize';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';

export type SearchPageHeaderTopbarProps = {
  actions?: ReactNode;
  isActionsVisible?: boolean;
};

export const SearchPageHeaderTopbar = memo(({ actions, isActionsVisible }: SearchPageHeaderTopbarProps) => {
  const resizeRef = useHeaderResize();
  const { showExactSearchHeaders } = usePrivateSearchUIHelpers();
  const isRightPanelOpen = useWorkspaceRightPanelOpen();

  const showActions = !showExactSearchHeaders ? true : !!isActionsVisible;

  return (
    <div
      className="group sticky top-0 z-30 flex min-h-14 shrink-0 items-center bg-grey-1/85 backdrop-blur-[18px]"
      ref={resizeRef}
    >
      <div className={cn('flex w-full items-center justify-between pr-4', !showExactSearchHeaders && 'pl-4')}>
        {isRightPanelOpen ? (
          <div className="min-w-0 flex-1 px-4">
            <PrivatePageSearch
              compact
              trackLocation="search-page"
              placeholder="Search all assets & files"
              hideFilterBar
            />
          </div>
        ) : (
          <>
            {showExactSearchHeaders && (
              <PrivateFiltersBar className="overflow-visible p-4" trackLocation="search-page" />
            )}
            {!showExactSearchHeaders && (
              <h1
                data-testid="SEARCH_PAGE_HEADER_TOP_BAR_TITLE"
                className="px-2 py-1 font-semibold text-12 text-grey-11"
              >
                Searching all assets & files
              </h1>
            )}
          </>
        )}
        <Presence present={showActions}>
          <div
            className="data-[state=active]:animation-duration-200 data-[state=hidden]:animation-duration-100 data-[state=hidden]:fade-out data-[state=active]:slide-in-from-right-12 data-[state=hidden]:slide-out-to-right-12 shrink-0 data-[state=active]:animate-in data-[state=hidden]:animate-out"
            data-state={showActions ? 'active' : 'hidden'}
          >
            {actions}
          </div>
        </Presence>
      </div>
    </div>
  );
});

SearchPageHeaderTopbar.displayName = 'SearchPageHeaderTopbar';
